import { 
  BonusSection, 
  NewBonusSection, 
  LegacyBonusSection,
  LegacyBonusItem
} from '@/types/analysisTypes';

/**
 * Convert legacy bonus format to new bonus format
 */
export function convertLegacyToNewFormat(legacySection: LegacyBonusSection): NewBonusSection {
  const newSection: NewBonusSection = {
    expert_insight: {
      title: "Expert Insight",
      intro: "",
      key_takeaways: [],
      conclusion: ""
    },
    unexpected_analogy: {
      title: "Unexpected Analogy",
      intro: "",
      key_points: [],
      conclusion: ""
    },
    challenged_assumption: {
      title: "Challenged Assumption",
      intro: "",
      key_reasons: [],
      solution: ""
    },
    insider_tip: {
      title: "Insider Tip",
      intro: "",
      step_by_step: [],
      final_takeaway: ""
    }
  };
  
  // Convert expert_insight if it exists
  if (legacySection.expert_insight) {
    const insight = legacySection.expert_insight;
    newSection.expert_insight = {
      title: "Expert Insight",
      intro: typeof insight === 'object' ? insight.example || "" : "",
      key_takeaways: ["Key insight from the analysis"],
      conclusion: "This insight provides valuable perspective on the content."
    };
  }
  
  // Convert unexpected_analogy if it exists
  if (legacySection.unexpected_analogy) {
    const analogy = legacySection.unexpected_analogy;
    newSection.unexpected_analogy = {
      title: "Unexpected Analogy",
      intro: typeof analogy === 'object' ? analogy.example || "" : "",
      key_points: ["Relevant connection to the topic"],
      conclusion: "This analogy helps understand the concept in a different way."
    };
  }
  
  // Convert challenged_assumption if it exists
  if (legacySection.challenged_assumption) {
    const assumption = legacySection.challenged_assumption;
    newSection.challenged_assumption = {
      title: "Challenged Assumption",
      intro: typeof assumption === 'object' ? assumption.example || "" : "",
      key_reasons: ["This assumption doesn't hold in all contexts"],
      solution: "A more nuanced approach is recommended."
    };
  }
  
  // Convert insider_tip if it exists
  if (legacySection.insider_tip) {
    const tip = legacySection.insider_tip;
    newSection.insider_tip = {
      title: "Insider Tip",
      intro: typeof tip === 'object' ? tip.example || "" : "",
      step_by_step: ["Implement this approach to improve results"],
      final_takeaway: "This tip can significantly enhance outcomes."
    };
  }
  
  // Handle simplified_explanation which we'll keep as is
  if (legacySection.simplified_explanation) {
    newSection.simplified_explanation = legacySection.simplified_explanation;
  }
  
  return newSection;
}

/**
 * Create a structured bonus section from a single creative insight string
 */
export function createStructuredInsightFromString(insightText: string): NewBonusSection {
  return {
    expert_insight: {
      title: "Expert Analysis",
      intro: insightText,
      key_takeaways: [
        "This insight was automatically extracted from the analysis",
        "Consider requesting a more detailed analysis for comprehensive insights",
        "The original format provided limited structured data"
      ],
      conclusion: "This represents the core insight from the analysis."
    },
    unexpected_analogy: {
      title: "Content Connections",
      intro: "The content could benefit from unexpected analogies to explain complex concepts.",
      key_points: [
        "Analogies help readers understand difficult topics",
        "They create memorable anchor points for information retention",
        "They bridge knowledge gaps between expert and beginner audiences"
      ],
      conclusion: "Consider adding relevant analogies to enhance comprehension."
    },
    challenged_assumption: {
      title: "Common Content Assumptions",
      intro: "Content creators often make assumptions about their audience's knowledge level.",
      key_reasons: [
        "Audiences have varied expertise levels",
        "Technical knowledge varies significantly across readers",
        "Prior exposure to the topic affects comprehension"
      ],
      solution: "Create content with multiple entry points for different knowledge levels."
    },
    insider_tip: {
      title: "Content Enhancement Strategy",
      intro: "Expert content can be improved with strategic information architecture.",
      step_by_step: [
        "Identify key concepts that need additional explanation",
        "Create visual aids for complex topics",
        "Add practical examples that illustrate abstract concepts"
      ],
      final_takeaway: "Structuring content for multiple audience levels improves engagement."
    }
  };
}
